import { finish } from '@tol/core/work';
// A GTFS city's network and trains in the shapes the core and renderer use:
// network.json becomes a Network, and each live trip from /trains a
// TrainPrediction. No DOM.
//
// London has to work out which train is which from station countdowns. GTFS
// feeds say it outright: every trip lists its calls with times, and most say
// where the train is ("stopped at", "in transit to"). So a trip's last
// station and next one come straight from the feed, and only a feed that
// drops calls already made (New York's subway) needs the network to find
// where a train came from.

import * as Core from '@tol/core/core';
import type { Arrival, ComputedTrains, Network, NetworkLine, RouteDirection, StationId, Stop } from '@tol/core/types';
import type { TrainPrediction } from '@tol/core/gps-types';
import { NO_CLOSURES, hopClosed } from './closures.ts';
import type { Closures } from './closures.ts';
import type { GtfsFeedState, GtfsTrains } from './types.ts';
import type { GtfsTrip } from './estimated-types.ts';
import { DIRECTION, RAILROAD_REACH } from './network-file.ts';

export { gtfsNetwork, gtfsNetworkAsync, isNetworkFile, RAILROAD_REACH } from './network-file.ts';

export function isTrains(v: unknown): v is GtfsTrains {
  return (
    typeof v === 'object' &&
    v !== null &&
    'trips' in v &&
    Array.isArray(v.trips) &&
    'time' in v &&
    (!('networkBuilt' in v) || typeof v.networkBuilt === 'string')
  );
}

// While a feed is loading (its server's first refresh after a quiet spell,
// @tol/gtfs/feed partialMs), ask again this soon: its answer is from memory.
export const LOADING_POLL_MS = 1500;

// The feeds that failed, and whether any is still loading.
export function feedsState(feeds: Readonly<Record<string, GtfsFeedState>>): {
  readonly down: readonly string[];
  readonly loading: boolean;
} {
  const all = Object.entries(feeds);
  return {
    down: all
      .filter(([, f]: readonly [string, GtfsFeedState]) => !f.ok && f.loading !== true)
      .map(([id]: readonly [string, GtfsFeedState]) => id),
    loading: all.some(([, f]: readonly [string, GtfsFeedState]) => f.loading === true),
  };
}

// A trip's calls on stations its line has, as ms times.
interface Call {
  readonly sid: StationId;
  readonly arr: number;
  readonly dep: number;
  // arr, else dep.
  readonly t: number;
  // A station the trip runs through without calling: the feed says so
  // (GtfsCall's pass), or it's filled in between two calls that aren't
  // neighbors on the network (see fillGaps).
  readonly via?: true;
}

// Stations between a and b on the line's network, fewest hops first, or
// null past maxHops.
function between(L: NetworkLine, a: StationId, b: StationId, maxHops = 12): StationId[] | null {
  const from = new Map<StationId, StationId>([[a, a]]);
  let frontier = [a];
  for (let hop = 0; hop < maxHops && frontier.length > 0; hop++) {
    const next: StationId[] = [];
    for (const x of frontier) {
      for (const y of L.adj.get(x) ?? []) {
        if (from.has(y)) continue;
        from.set(y, x);
        if (y === b) {
          const path: StationId[] = [];
          for (let z = from.get(b); z !== undefined && z !== a; z = from.get(z)) path.unshift(z);
          return path;
        }
        next.push(y);
      }
    }
    frontier = next;
  }
  return null;
}

// Service changes run trains past stations their line's patterns stop at:
// a 6 listed 14 St then Brooklyn Bridge, which aren't neighbors, so the core
// held it at 14 St and it jumped 2.7 km when the next call came up. Such a gap
// gets the stations between, timed by distance, as stations it runs through.
function fillGaps(net: Network, L: NetworkLine, lineId: string, calls: readonly Call[]): Call[] {
  const out: Call[] = [];
  for (const c of calls) {
    const prev = out.at(-1);
    const leave = prev ? prev.dep || prev.t : 0;
    const path =
      prev && leave && c.t > leave && L.adj.get(prev.sid)?.has(c.sid) !== true ? between(L, prev.sid, c.sid) : null;
    if (prev && path && path.length > 0) {
      const hops = [prev.sid, ...path, c.sid];
      const lengths = hops.slice(1).map((sid, i) => Core.segLength(net, lineId, hops[i] ?? sid, sid));
      const total = lengths.reduce((x, y) => x + y, 0) || 1;
      let run = 0;
      for (const [i, sid] of path.entries()) {
        run += lengths[i] ?? 0;
        const t = Math.round(leave + ((c.t - leave) * run) / total);
        out.push({ sid, arr: t, dep: t, t, via: true });
      }
    }
    out.push(c);
  }
  return out;
}

// At each station a trip is run on to (runOn).
const DWELL_MS = 30_000;

// A trip whose feed may stop short of its end (GtfsTrip.more) runs on along
// its shape's pattern, else the busiest one this way through its last two
// calls, timed by the run times: 511 leaves out BART's last stop (every
// Yellow line train was "to San Bruno", and stopped there), and others'
// calls past an hour or so.
function runOn(
  net: Network,
  trip: GtfsTrip,
  calls: readonly Call[],
  runTime: Core.RunTime,
  shapes: ShapePatterns,
): readonly Call[] {
  const L = net.lines[trip.line];
  const last = calls.at(-1);
  if (trip.complete === true || trip.more !== true || !L || !last || (last.dep || last.t) === 0) return calls;
  const prev = calls.at(-2)?.sid;
  const dir = trip.dir === null ? '' : (DIRECTION[trip.dir] ?? '');
  const through = (ids: readonly StationId[]): boolean => {
    const k = ids.indexOf(last.sid);
    return k >= 0 && (prev === undefined || ids.slice(0, k).includes(prev));
  };
  const shaped = trip.shape === undefined ? undefined : shapes[trip.shape];
  const pattern =
    shaped && through(shaped) ? shaped : L.seqs.find((seq) => (dir === '' || seq.dir === dir) && through(seq.ids))?.ids;
  if (!pattern) return calls;
  const out = [...calls];
  let from = last.sid;
  let t = last.dep || last.t;
  for (const sid of pattern.slice(pattern.indexOf(last.sid) + 1)) {
    if (L.adj.get(from)?.has(sid) !== true) break;
    const arr = t + runTime(trip.line, from, sid) * 1000;
    out.push({ sid, arr, dep: arr + DWELL_MS, t: arr });
    t = arr + DWELL_MS;
    from = sid;
  }
  return out;
}

function callsOf(net: Network, trip: GtfsTrip): Call[] {
  const L = net.lines[trip.line];
  if (!L) return [];
  const out: Call[] = [];
  for (const [sid, arr, dep, pass] of trip.calls) {
    if (!L.ids.has(sid)) continue;
    const a = arr * 1000;
    const d = dep * 1000;
    out.push({ sid, arr: a, dep: d, t: a || d, ...(pass === 1 ? { via: true as const } : {}) });
  }
  return fillGaps(net, L, trip.line, out);
}

// Median run time between neighbors (line|a|b, s) the calls show, if any.
type Learned = (lineId: string, a: StationId, b: StationId) => number | undefined;

// Median run time between neighbors (line|a|b, s), from every trip's
// upcoming calls (`learned`), else the core's estimate (`runTime`).

function* learnRunTimesSteps(
  net: Network,
  trips: readonly (readonly Call[])[],
  lines: readonly string[],
): Generator<void, { readonly runTime: Core.RunTime; readonly learned: Learned }, void> {
  const obs = new Map<string, number[]>();
  for (const [i, calls] of trips.entries()) {
    const lineId = lines[i] ?? '';
    const adj = net.lines[lineId]?.adj;
    for (let j = 1; j < calls.length; j++) {
      const a = calls[j - 1];
      const b = calls[j];
      if (!a || !b || adj?.get(a.sid)?.has(b.sid) !== true) continue;
      const gap = (b.t - (a.dep || a.t)) / 1000;
      if (!(gap > 15 && gap < 3600)) continue;
      const k = `${lineId}|${a.sid}|${b.sid}`;
      let v = obs.get(k);
      if (!v) obs.set(k, (v = []));
      v.push(gap);
    }
    yield;
  }
  const med = new Map<string, number>();
  for (const [k, v] of obs) {
    v.sort((x, y) => x - y);
    const m = v[v.length >> 1];
    if (m !== undefined) med.set(k, m);
    yield;
  }
  const learned: Learned = (lineId, a, b) => med.get(`${lineId}|${a}|${b}`);
  return { runTime: (lineId, a, b) => learned(lineId, a, b) ?? Core.segTime(net, a, b, lineId), learned };
}

// GPS further than this from the trip's track is ignored.
const GPS_OFF_TRACK = 300;

// Metro-North's GPS jumps: a train heading for Grand Central reported
// 40.912, 40.901, 40.942, 40.942, 40.896 (a 4.5 km step back, twice, then on
// course again). So a fix only counts when it's on the trip's track and moves
// the train forward at a speed a train can do; otherwise the last good fix
// keeps placing it (for up to GPS_KEEP_S). After GPS_REFUSALS refusals in a
// row, the new fixes win: the old one was the bad one.
//
// Fix times are rough: an LIRR train's fixes 118 s apart were 6 km apart
// along the track (51 m/s, faster than it runs), so the speed limit is loose.
// The outliers it's for are far faster (4.5 km in 15 s).
const GPS_MAX_SPEED = 70;
const GPS_BACK = 300;
const GPS_REFUSALS = 3;
const GPS_KEEP_S = 120;
// A trip absent from the feed this long is forgotten (its memory freed).
const GPS_FORGET_S = 600;
// A fix never places a train on a hop it isn't due to start for longer than
// this: Muni's vehicle feed names the trip a car will run next while it's
// still finishing the last one, and an L car at Embarcadero was placed on its
// next trip's last hop, 35 minutes early, then snapped 11 km west.
const GPS_AHEAD_MS = 10 * 60_000;
// A fix this close to a station is at it: 150 m, or less on a short hop (1%
// of the hop, as before, let an ACE train 63 m from San Jose Diridon set off
// 11 minutes before it was due out).
const GPS_AT_STATION = 150;
const GPS_AT_STATION_F = 0.05;

// A fix placed on a trip: on the hop into call `to`, a fraction f along it,
// with `left` meters of track to the trip's last listed call.
interface Placed {
  readonly lat: number;
  readonly lon: number;
  readonly from: StationId;
  readonly to: StationId;
  readonly f: number;
  readonly gt: number;
  readonly timestamped: boolean;
  readonly left: number;
  readonly end: StationId;
  // m/s along the track since the fixes before, once there are two.
  readonly speed?: number;
}

// Trip id -> its last good fix, how many distinct newer fixes since were
// refused, and the newest fix time seen. A repeated snapshot must not count
// again: city polling recomputes equal-timestamp snapshots.
// Changed in place by goodFix (allowed as mutable in .oxlintrc.json).
export interface GpsMemory extends Map<
  string,
  { readonly fix: Placed; readonly refused: number; readonly seen: number }
> {
  readonly [Symbol.toStringTag]: string;
}

export function createGpsMemory(): GpsMemory {
  return new Map();
}

function freshGps(trip: GtfsTrip, now: number): boolean {
  const time = trip.gt ?? trip.gpsObservedAt;
  return (
    trip.lat !== undefined &&
    trip.lon !== undefined &&
    time !== undefined &&
    time > 0 &&
    time <= now / 1000 + 30 &&
    now / 1000 - time < (trip.gpsMaxExtrapolationS === undefined ? 180 : 300)
  );
}

function place(net: Network, trip: GtfsTrip, calls: readonly Call[], now: number): Placed | null {
  const L = net.lines[trip.line];
  const time = trip.gt ?? trip.gpsObservedAt;
  if (!L || trip.lat === undefined || trip.lon === undefined || time === undefined || !freshGps(trip, now)) return null;
  const gps = { lat: trip.lat, lon: trip.lon };
  let best: { i: number; f: number; d: number } | null = null;
  for (let i = 1; i < calls.length; i++) {
    const a = calls[i - 1];
    const b = calls[i];
    if (!a || !b || L.adj.get(a.sid)?.has(b.sid) !== true) continue;
    if (
      (trip.followEstimatedPosition === true || trip.followObservedPosition === true) &&
      trip.positionHop !== undefined &&
      (trip.positionHop[0] !== a.sid || trip.positionHop[1] !== b.sid)
    )
      continue;
    if ((a.dep || a.t) > now + GPS_AHEAD_MS) break;
    const { f, d } = Core.projectOnto(net, trip.line, a.sid, b.sid, gps);
    if (!best || d < best.d) best = { i, f, d };
  }
  const from = best ? calls[best.i - 1] : undefined;
  const to = best ? calls[best.i] : undefined;
  if (!best || !from || !to || best.d > GPS_OFF_TRACK) return null;
  let left = (1 - best.f) * Core.segLength(net, trip.line, from.sid, to.sid);
  for (let j = best.i + 1; j < calls.length; j++) {
    const a = calls[j - 1];
    const b = calls[j];
    if (a && b) left += Core.segLength(net, trip.line, a.sid, b.sid);
  }
  return {
    lat: gps.lat,
    lon: gps.lon,
    from: from.sid,
    to: to.sid,
    f: best.f,
    gt: time,
    timestamped: trip.gt !== undefined,
    left,
    end: calls.at(-1)?.sid ?? to.sid,
  };
}

// The index of the call `to` reached from `from` (i, with calls[i - 1] from),
// or -1. By the pair, not the station alone: a loop calls at a station twice.
function hopIndex(calls: readonly Call[], from: StationId, to: StationId): number {
  for (let i = 1; i < calls.length; i++) if (calls[i]?.sid === to && calls[i - 1]?.sid === from) return i;
  return -1;
}

const gpsKey = (trip: GtfsTrip): string =>
  trip.gpsMaxExtrapolationS !== undefined && trip.journey !== undefined
    ? JSON.stringify([trip.line, trip.journey])
    : trip.id;

// The fix to place the trip by, if any, updating `memory`.
function goodFix(net: Network, trip: GtfsTrip, calls: readonly Call[], memory: GpsMemory, now: number): Placed | null {
  const fix = place(net, trip, calls, now);
  const key = gpsKey(trip);
  const kept = memory.get(key);
  const changedClock =
    trip.gpsMaxExtrapolationS !== undefined &&
    fix !== null &&
    kept !== undefined &&
    fix.timestamped !== kept.fix.timestamped;
  const last = changedClock ? undefined : kept?.fix;
  const seen = changedClock ? Number.NEGATIVE_INFINITY : (kept?.seen ?? last?.gt ?? Number.NEGATIVE_INFINITY);
  if (fix && (!last || fix.gt > last.gt) && fix.gt > seen) {
    const bounded = trip.gpsMaxExtrapolationS !== undefined;
    const dt = last ? fix.gt - last.gt : 1;
    let moved = last ? last.left - fix.left : 0;
    if (bounded && last && last.end !== fix.end) {
      moved =
        last.from === fix.from && last.to === fix.to
          ? (fix.f - last.f) * Core.segLength(net, trip.line, fix.from, fix.to)
          : last.to === fix.from
            ? (1 - last.f) * Core.segLength(net, trip.line, last.from, last.to) +
              fix.f * Core.segLength(net, trip.line, fix.from, fix.to)
            : Core.dist(last, fix);
    }
    const plausible =
      !last ||
      (bounded && trip.positionEstimated === true) ||
      (bounded
        ? moved / Math.max(1, dt) <= GPS_MAX_SPEED
        : fix.left <= last.left + GPS_BACK && (last.left - fix.left) / Math.max(1, dt) <= GPS_MAX_SPEED);
    const refused = plausible ? 0 : (kept?.refused ?? 0) + 1;
    if (plausible || refused >= GPS_REFUSALS) {
      // Smoothed over successive fixes; restarted after a refused run.
      const v =
        last && plausible && dt >= (bounded ? 1 : 20) && (!bounded || (moved >= 0 && moved / dt <= GPS_MAX_SPEED))
          ? Math.max(0, moved) / dt
          : undefined;
      const measured =
        bounded &&
        trip.positionEstimated !== true &&
        trip.gpsSpeedMps !== undefined &&
        Number.isFinite(trip.gpsSpeedMps) &&
        trip.gpsSpeedMps >= 0 &&
        trip.gpsSpeedMps <= GPS_MAX_SPEED
          ? trip.gpsSpeedMps
          : v;
      const speed =
        measured === undefined || !plausible
          ? undefined
          : bounded
            ? measured
            : last?.speed === undefined
              ? measured
              : (last.speed + measured) / 2;
      const placed = speed === undefined ? fix : { ...fix, speed };
      memory.set(key, { fix: placed, refused: 0, seen: fix.gt });
      return placed;
    }
    memory.set(key, { fix: last ?? fix, refused, seen: fix.gt });
  }
  // A slow feed may repeat its newest fix (511 polls every 150 s), but
  // repetition never extends the server's three-minute freshness boundary.
  const age = trip.gpsMaxExtrapolationS === undefined ? 180 : 300;
  if (
    !last ||
    ((trip.followEstimatedPosition === true || trip.followObservedPosition === true) &&
      trip.positionHop !== undefined &&
      (trip.positionHop[0] !== last.from || trip.positionHop[1] !== last.to)) ||
    now / 1000 - last.gt >= age ||
    (now / 1000 - last.gt > GPS_KEEP_S && (trip.gt ?? trip.gpsObservedAt) !== last.gt)
  )
    return null;
  // Still on a hop this trip lists.
  return hopIndex(calls, last.from, last.to) > 0 ? last : null;
}

// A trip that hasn't left its first station is shown this long before it does.
const START_SOON = 90_000;

// A call as the core's Stop: its departure when the feed gives one after
// its arrival, and whether the train only runs through. `late` (ms) moves it.
function stopOf(c: Call, eta: number, late = 0): Stop {
  const dep = c.arr > 0 && c.dep > c.arr ? c.dep + late : undefined;
  return { sid: c.sid, eta, ...(dep !== undefined && dep > eta ? { dep } : {}), ...(c.via ? { pass: true } : {}) };
}

function stopsFrom(calls: readonly Call[], i: number, firstEta: number): Stop[] {
  return calls.slice(i).map((c, j) => stopOf(c, j === 0 ? firstEta : c.t));
}

// A station on a trip's card: its time (ms; 0 when the feed has none, as
// for stations already passed that the feed no longer lists), and whether
// the trip only runs through it.
export interface TripStop {
  readonly sid: StationId;
  readonly t: number;
  readonly via?: true;
}

// The whole trip, first station to last. Some feeds list every call, others
// only those ahead, so the stations before come from the trip's shape's
// pattern.
export function tripStops(net: Network, trip: GtfsTrip, shapes: ShapePatterns = {}): TripStop[] {
  const run = (lineId: string, a: StationId, b: StationId): number => Core.segTime(net, a, b, lineId);
  const calls: TripStop[] = runOn(net, trip, callsOf(net, trip), run, shapes).map((c) =>
    c.via ? { sid: c.sid, t: c.t, via: true } : { sid: c.sid, t: c.t },
  );
  const first = calls[0];
  if (!first || trip.complete === true) return calls;
  // Its shape's pattern, else the busiest one this way that has its first
  // two calls in a row (the F's and 7's trip ids name no known shape).
  const shaped = trip.shape === undefined ? undefined : shapes[trip.shape];
  const dir = trip.dir === null ? '' : (DIRECTION[trip.dir] ?? '');
  const second = calls[1]?.sid;
  const pattern =
    shaped && shaped.includes(first.sid)
      ? shaped
      : net.lines[trip.line]?.seqs.find((seq) => {
          const k = seq.ids.indexOf(first.sid);
          return (dir === '' || seq.dir === dir) && k > 0 && (second === undefined || seq.ids[k + 1] === second);
        })?.ids;
  const j = pattern ? pattern.indexOf(first.sid) : -1;
  if (!pattern || j <= 0) return calls;
  return [...pattern.slice(0, j).map((sid) => ({ sid, t: 0 })), ...calls];
}

// Whether a station is on the line's patterns this way (dir, '' for either)
// but starts none of them.
function midLine(L: NetworkLine, sid: StationId, dir: RouteDirection | ''): boolean {
  const seqs = L.seqs.filter((seq) => dir === '' || seq.dir === dir);
  return seqs.some((seq) => seq.ids.indexOf(sid) > 0) && !seqs.some((seq) => seq.ids[0] === sid);
}

// Whether some pattern this way (dir, '' for either) comes into a station:
// one where every trip starts has no hop into it.
function reached(L: NetworkLine, sid: StationId, dir: RouteDirection | ''): boolean {
  return L.seqs.some((seq) => (dir === '' || seq.dir === dir) && seq.ids.indexOf(sid) > 0);
}

// One trip's prediction, or null when it isn't on the network now.
// Shape id -> stations, from network.json.
export type ShapePatterns = Readonly<Record<string, readonly string[]>>;

// A prediction its GPS fix placed says so (TrainPrediction.gps).
const byFix = (p: Readonly<TrainPrediction> | null): TrainPrediction | null => (p ? { ...p, gps: true } : null);

function predict(
  net: Network,
  trip: GtfsTrip,
  calls: readonly Call[],
  times: { readonly runTime: Core.RunTime; readonly learned: Learned },
  now: number,
  shapes: ShapePatterns,
  memory?: GpsMemory,
): TrainPrediction | null {
  const { runTime, learned } = times;
  const L = net.lines[trip.line];
  if (!L) return null;
  const dest = net.stations.get(trip.dest)?.name ?? net.stations.get(calls.at(-1)?.sid ?? '')?.name ?? '';
  const reach =
    trip.motionTracker === true && trip.line.startsWith('train-')
      ? Math.max(trip.reach ?? 1, RAILROAD_REACH)
      : trip.reach;
  const base = {
    // Long, fast hops get more (see TrainPrediction.reach).
    ...(reach === undefined ? {} : { reach }),
    ...(trip.scheduled === true ? { scheduled: true as const } : {}),
    key: trip.id,
    ...(trip.followEstimatedPosition === true ? { followEstimatedPosition: true as const } : {}),
    ...(trip.followObservedPosition === true ? { followObservedPosition: true as const } : {}),
    lineId: trip.line,
    dest,
    towards: '',
    loc: '',
    platform: '',
    vid: trip.vid,
    waiting: false,
    ...(trip.motionTracker === true || (trip.gpsMaxExtrapolationS !== undefined && trip.positionEstimated === true)
      ? { motionTracker: true as const }
      : {}),
    ...(trip.holdAt === undefined ? {} : { holdAt: trip.holdAt }),
    ...(trip.holdTime === undefined ? {} : { holdTime: trip.holdTime * 1000 }),
    ...(trip.journey === undefined ? {} : { journey: trip.journey }),
  };
  const unbound = (): TrainPrediction | null => {
    const at = trip.gt ?? trip.gpsObservedAt;
    if (
      trip.gpsMaxExtrapolationS === undefined ||
      (trip.positionEstimated !== true && trip.followObservedPosition !== true) ||
      !freshGps(trip, now) ||
      trip.lat === undefined ||
      trip.lon === undefined ||
      !Number.isFinite(trip.lat) ||
      !Number.isFinite(trip.lon) ||
      Math.abs(trip.lat) > 90 ||
      Math.abs(trip.lon) > 180 ||
      at === undefined
    )
      return null;
    return {
      ...base,
      P: null,
      N: '',
      eta: at * 1000,
      segT: 0,
      stops: [],
      gpsPoint: { lat: trip.lat, lon: trip.lon, at: at * 1000 },
      ...(trip.followObservedPosition === true && trip.positionEstimated !== true && trip.gt !== undefined
        ? { gps: true as const }
        : {}),
    };
  };
  if (calls.length === 0) return unbound();
  const dir = trip.dir === null ? '' : (DIRECTION[trip.dir] ?? '');
  const pattern = trip.shape === undefined ? undefined : shapes[trip.shape];
  const prevOf = (i: number, N: StationId): StationId | null => {
    const before = calls[i - 1];
    if (before) return before.sid;
    const j = pattern ? pattern.indexOf(N) : -1;
    const fromPattern = j > 0 ? pattern?.[j - 1] : undefined;
    if (fromPattern !== undefined && L.adj.get(fromPattern)?.has(N) === true) return fromPattern;
    // No shape (the 7's trips are just "7..S"): the busiest pattern this way
    // with N followed by the next call. Guessing from the network alone
    // flipped the 7 between its local and express stations as calls came up.
    const after = calls[i + 1]?.sid;
    const candidates = new Set<StationId>();
    for (const seq of L.seqs) {
      if (dir !== '' && seq.dir !== dir) continue;
      const k = seq.ids.indexOf(N);
      const last = k > 0 ? seq.ids[k - 1] : undefined;
      if (last !== undefined && (after === undefined || seq.ids[k + 1] === after)) candidates.add(last);
    }
    if (candidates.size === 1) return [...candidates][0] ?? null;
    if (candidates.size > 1 && trip.lat !== undefined && trip.lon !== undefined) {
      let nearest: { readonly sid: StationId; readonly d: number } | undefined;
      for (const sid of candidates) {
        const { d } = Core.projectOnto(net, trip.line, sid, N, { lat: trip.lat, lon: trip.lon });
        if (!nearest || d < nearest.d) nearest = { sid, d };
      }
      if (nearest && nearest.d <= GPS_OFF_TRACK) return nearest.sid;
    }
    if (candidates.size > 1) return null;
    return Core.findPrev(net, L, N, after, dir, '');
  };
  const moving = (i: number): TrainPrediction | null => {
    const c = calls[i];
    if (!c) return null;
    const P = prevOf(i, c.sid);
    const before = calls[i - 1];
    // The feed's own times for this hop, when it lists the call before.
    const gap = before ? (c.t - (before.dep || before.t)) / 1000 : 0;
    const segT =
      P === null ? 0 : gap > 15 && (gap < 3600 || trip.complete === true) ? gap : runTime(trip.line, P, c.sid);
    return { ...base, P, N: c.sid, eta: c.t, segT, stops: stopsFrom(calls, i, c.t) };
  };
  // `late` (ms) moves the calls from here on later.
  const stopped = (i: number, late = 0): TrainPrediction | null => {
    const c = calls[i];
    if (!c) return null;
    // At the platform until it's due out; the core then follows the calls.
    const out = (c.dep || c.t) + late;
    const stops = calls.slice(i).map((x, j) => (j === 0 ? { sid: x.sid, eta: out } : stopOf(x, x.t + late, late)));
    return { ...base, P: null, N: c.sid, eta: out, segT: 0, stops };
  };
  const held = calls.findIndex(
    (c) => c.sid === trip.holdAt && (trip.holdTime === undefined || c.dep === trip.holdTime * 1000),
  );
  const heldCall = calls[held];
  if (heldCall && now >= heldCall.t) return stopped(held);

  // GPS (the LIRR's) says which hop the train is on and how far along, which
  // beats working it out from times on long hops. From the fix it runs at the
  // hop's usual speed, not to the feed's arrival time: those lag. A Ronkonkoma
  // branch train still had 4.5 min to Central Islip in the feed when its GPS
  // was already there, and 21 s in the next response.
  //
  // A feed that lists only the calls ahead (Capitol Corridor's, Muni's) has
  // its train on the way to the first one, so a fix can also be on the hop
  // there from the station before. Without it a late Capitol Corridor train
  // ran to Suisun by the feed's times, and on, while its GPS still had it
  // 8 km short, and snapped back.
  //
  // A feed that lists the calls already made (NJ Transit's) starts at the
  // trip's origin, which no train comes into. There, the station before
  // would be made up from the network: New York Penn's express neighbor
  // South Orange, whose hop in runs the same track as the hop out, and took
  // the fix of a train leaving New York, drawn heading back into it.
  const firstCall = calls[0];
  const approach =
    firstCall && trip.lat !== undefined && reached(L, firstCall.sid, dir) ? prevOf(0, firstCall.sid) : null;
  const gpsCalls: readonly Call[] = approach === null ? calls : [{ sid: approach, arr: 0, dep: 0, t: 0 }, ...calls];
  const shift = gpsCalls.length - calls.length;
  const fix = memory ? goodFix(net, trip, gpsCalls, memory, now) : null;
  if (fix) {
    const i = hopIndex(gpsCalls, fix.from, fix.to);
    const a = gpsCalls[i - 1];
    const b = gpsCalls[i];
    if (a && b) {
      const taken = fix.gt * 1000;
      const hop = Core.segLength(net, trip.line, a.sid, b.sid);
      const near = Math.min(GPS_AT_STATION, GPS_AT_STATION_F * hop);
      if (trip.gpsMaxExtrapolationS !== undefined) {
        const scheduledSpeed =
          hop /
          Math.max(
            1,
            trip.positionEstimated === true && a.t === 0
              ? runTime(trip.line, a.sid, b.sid)
              : ((b.arr || b.t) - (a.dep || a.t)) / 1000,
          );
        const atStop = fix.f * hop <= near || (1 - fix.f) * hop <= near;
        const speed =
          trip.followObservedPosition === true && trip.gt !== undefined
            ? Math.min(25, fix.speed ?? 0)
            : trip.gt === undefined || atStop || (fix.speed !== undefined && fix.speed < 0.5)
              ? 0
              : Math.min(25, fix.speed ?? scheduledSpeed);
        const segT = hop / Math.max(0.5, speed);
        const eta = taken + (1 - fix.f) * segT * 1000;
        const capSpeed = Math.min(25, Math.max(speed, scheduledSpeed));
        return {
          ...base,
          P: a.sid,
          N: b.sid,
          eta,
          segT,
          stops: gpsCalls.slice(i).map((c) => stopOf(c, c.t)),
          ...(trip.positionEstimated !== true && trip.gpsObservedAt === undefined ? { gps: true as const } : {}),
          gpsMotion: {
            from: a.sid,
            to: b.sid,
            f: fix.f,
            at: taken,
            speed,
            capSpeed,
            maxS: trip.gpsMaxExtrapolationS,
            ...(trip.positionEstimated === true || trip.followObservedPosition === true
              ? {
                  scheduledSpeed: Math.min(25, scheduledSpeed),
                  measuredSpeed: fix.speed,
                  nextStopDistance: (1 - fix.f) * hop,
                  speed:
                    trip.followObservedPosition === true
                      ? speed
                      : Math.min(
                          25,
                          fix.speed === undefined
                            ? scheduledSpeed
                            : fix.speed === 0
                              ? 0
                              : (fix.speed + scheduledSpeed) / 2,
                        ),
                }
              : {}),
          },
        };
      }
      if (fix.f * hop <= near && (a.dep || a.t) > now) return byFix(stopped(i - 1 - shift));
      // At the station when it was due out: it leaves that much late at least.
      if ((1 - fix.f) * hop <= near) return byFix(stopped(i - shift, Math.max(0, taken - (b.dep || b.t))));
      // On the way to the first call no trip may list the hop, so there may
      // be no run time for it but the core's guess from its length (50 min
      // for Capitol Corridor's 40 km from Davis: each new fix was 3 km ahead
      // of the train), and the feed's arrival time is no better (a late
      // train was due at Suisun "in 12 s" for minutes, 7 km out). The
      // train's own speed between fixes is.
      const approaching = i === shift && shift > 0;
      const segT =
        (approaching ? learned(trip.line, a.sid, b.sid) : undefined) ??
        (approaching && fix.speed !== undefined
          ? hop / Math.min(GPS_MAX_SPEED, Math.max(3, fix.speed))
          : runTime(trip.line, a.sid, b.sid));
      const eta = taken + (1 - fix.f) * segT * 1000;
      // The stations after keep the feed's spacing, from the fix's time at
      // the next one: VTA's times ran two minutes ahead of its GPS, and the
      // train raced past its fix to catch up with them, then snapped back.
      const late = b.t > 0 ? eta - b.t : 0;
      const stops = gpsCalls.slice(i).map((c, j) => stopOf(c, j === 0 ? eta : c.t + late, late));
      return { ...base, P: a.sid, N: b.sid, eta, segT, stops, gps: true };
    }
  }

  if (trip.gpsMaxExtrapolationS !== undefined) {
    const fallback = trip.positionFallback;
    const retained = fallback ? predict(net, fallback, callsOf(net, fallback), times, now, shapes) : null;
    return retained
      ? {
          ...retained,
          ...(base.motionTracker === true ? { motionTracker: true as const } : {}),
          key: base.key,
          ...(trip.journey === undefined ? {} : { journey: trip.journey }),
        }
      : unbound();
  }

  const at = trip.at === undefined ? -1 : calls.findIndex((c) => c.sid === trip.at);
  if (trip.status === 'stopped') {
    if (at >= 0) return stopped(at);
    // Stopped at a station the trip no longer lists: it's leaving it for the
    // first one listed.
    const first = calls[0];
    if (first && trip.at !== undefined && L.adj.get(trip.at)?.has(first.sid) === true) {
      const P = trip.at;
      const segT = runTime(trip.line, P, first.sid);
      return { ...base, P, N: first.sid, eta: first.t, segT, stops: stopsFrom(calls, 0, first.t) };
    }
  }
  if ((trip.status === 'transit' || trip.status === 'incoming') && at >= 0) return moving(at);

  // Otherwise by the times: the first call still ahead. A call it's arrived
  // at but isn't due out of is where it stands (Sydney Trains' timetable has
  // 30 s at most stations): moving on to the next call as soon as the
  // arrival passed left the core only its 8 s for a call it's late for, and
  // drawn trains stood 8 s instead of 30 (New York's too).
  const next = calls.findIndex((c) => c.t > now);
  const here = next > 0 ? calls[next - 1] : undefined;
  if (here && here.via !== true && here.dep > now) return stopped(next - 1);
  if (next < 0) {
    // Every call is past: at its last station, until the core retires it.
    const last = calls.length - 1;
    return stopped(last);
  }
  if (next > 0) return moving(next);
  // Nothing made yet. A feed that lists only calls ahead: a trip whose train
  // reports a status is running, and so is one whose first call isn't where
  // any of its line's patterns start (BART reports no status, and a train
  // that had just left a station was drawn waiting at the next one, 4 km on);
  // otherwise it hasn't started.
  const first = calls[0];
  if (trip.status !== undefined || (first && midLine(L, first.sid, dir))) return moving(0);
  const leaves = first ? first.dep || first.t : 0;
  if (!first || leaves - now > START_SOON) return null;
  return { ...base, waiting: true, P: null, N: first.sid, eta: leaves, segT: 0, stops: stopsFrom(calls, 0, leaves) };
}

// Every trip's prediction, and each station's upcoming arrivals for its card.
// A scheduled trip (no live data) isn't shown where its line's alerts close
// it (closures.ts): not on a closed line, and neither the train nor an
// arrival on a hop over closed track. Live trips are shown regardless.
export function computeTrains(
  net: Network,
  data: GtfsTrains,
  now: number,
  shapes: ShapePatterns = {},
  memory?: GpsMemory,
  closures: Closures = NO_CLOSURES,
): ComputedTrains {
  return finish(computeTrainsSteps(net, data, now, shapes, memory, closures));
}

// The same conversion for synchronous callers and cooperatively scheduled
// browser feeds. All trips still contribute to the shared run-time estimates.
export function* computeTrainsSteps(
  net: Network,
  data: GtfsTrains,
  now: number,
  shapes: ShapePatterns = {},
  memory?: GpsMemory,
  closures: Closures = NO_CLOSURES,
): Generator<void, ComputedTrains, void> {
  // Trips removed by closure or expired vehicle-only evidence (ComputedTrains.retired).
  const retired = new Set<string>();
  const trips: GtfsTrip[] = [];
  const calls: Call[][] = [];
  const lines: string[] = [];
  const listedIds = new Set<string>();
  for (const t of data.trips) {
    if (net.lines[t.line] !== undefined) {
      if ((t.scheduled === true && closures.lines.has(t.line)) || (t.gpsOnly === true && !freshGps(t, now)))
        retired.add(t.id);
      else {
        trips.push(t);
        calls.push(callsOf(net, t));
        lines.push(t.line);
        listedIds.add(gpsKey(t));
      }
    }
    yield;
  }
  const { runTime, learned } = yield* learnRunTimesSteps(net, calls, lines);
  const trains: TrainPrediction[] = [];
  const byStation = new Map<StationId, Arrival[]>();
  for (const [i, listed] of trips.entries()) {
    const c = runOn(net, listed, calls[i] ?? [], runTime, shapes);
    const end = c.at(-1)?.sid;
    const trip = listed.more === true && end !== undefined ? { ...listed, dest: end } : listed;
    const closedHop = (from: StationId | null, to: StationId): boolean =>
      trip.scheduled === true && hopClosed(net, closures, trip.line, from, to);
    const predicted = predict(net, trip, c, { runTime, learned }, now, shapes, memory);
    const pred = predicted && closedHop(predicted.P, predicted.N) ? null : predicted;
    if (predicted && !pred) retired.add(predicted.key);
    if (pred) trains.push(pred);
    const dest = net.stations.get(trip.dest)?.name ?? '';
    // The station cards show the same times as the train: the prediction's
    // where it has one (GPS moves them), else the feed's. Its stops are its
    // last calls, in order: a loop's first and last calls share a station.
    const stops = pred?.stops ?? [];
    const skipped = c.length - stops.length;
    const last = c.findLastIndex((x) => x.sid === trip.dest);
    for (const [j, call] of c.entries()) {
      const st = stops[j - skipped];
      const eta = st?.sid === call.sid ? st.eta : call.t;
      // Not the call it ends at (nobody boards there), nor a station it runs
      // through. A loop's first call, at the same station, is a departure.
      if (eta < now - 30_000 || j === last || call.via === true) continue;
      // Nor one it can't reach: it comes over closed track, or starts there.
      if (closedHop(c[j - 1]?.sid ?? null, call.sid)) continue;
      let list = byStation.get(call.sid);
      if (!list) byStation.set(call.sid, (list = []));
      list.push({
        sid: call.sid,
        eta,
        lineId: trip.line,
        dir: trip.dir === null ? '' : (DIRECTION[trip.dir] ?? ''),
        loc: '',
        dest,
        towards: '',
        platform: '',
        vid: trip.vid,
        ...(trip.scheduled === true ? { scheduled: true as const } : {}),
      });
    }
    yield;
  }
  for (const list of byStation.values()) {
    list.sort((a, b) => a.eta - b.eta);
    yield;
  }
  if (memory) {
    for (const [id, m] of memory) {
      if (!listedIds.has(id) && now / 1000 - m.fix.gt > GPS_FORGET_S) memory.delete(id);
      yield;
    }
  }
  return retired.size > 0 ? { trains, byStation, retired } : { trains, byStation };
}
